import {
    orderedPredefinedLeafTypes,
    resolveLeafType,
} from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { parsePlainIntegerCodeText } from "@common/helpers/accounting";
import { ChartOfAccountsTemplateEditorRow } from "./editorRowTypes";

type CodeAndName = Pick<ChartOfAccountsTemplateEditorRow, "codeText" | "name">;
type TypeCodeAndName = Pick<
    ChartOfAccountsTemplateEditorRow,
    "type" | "codeText" | "name"
>;

const compareByName = (a: CodeAndName, b: CodeAndName): number =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

// Default ordering: partition by codeText, then name (case-insensitive). Typed
// on the minimal {codeText, name} shape so it sorts both editor rows and draft
// accounts. The partition (null last → parseable before unparseable → numeric
// within parseable → localeCompare within unparseable) keeps the comparator
// transitive on free-text codes, so 200 still sorts above 1000.
const compareByCodeThenName = (a: CodeAndName, b: CodeAndName): number => {
    if (a.codeText == null && b.codeText == null) {
        return compareByName(a, b);
    }
    if (a.codeText == null) {
        return 1;
    }
    if (b.codeText == null) {
        return -1;
    }
    const parsedA = parsePlainIntegerCodeText(a.codeText);
    const parsedB = parsePlainIntegerCodeText(b.codeText);
    if (parsedA != null && parsedB == null) {
        return -1;
    }
    if (parsedA == null && parsedB != null) {
        return 1;
    }
    if (parsedA != null && parsedB != null) {
        if (parsedA !== parsedB) {
            return parsedA - parsedB;
        }
        return compareByName(a, b);
    }
    const rawDiff = a.codeText.localeCompare(b.codeText);
    if (rawDiff !== 0) {
        return rawDiff;
    }
    return compareByName(a, b);
};

const leafOrderIndexByType = new Map<AccountType, number>(
    orderedPredefinedLeafTypes.map((type, index) => [type, index]),
);

const leafOrderOf = (type: AccountType): number =>
    leafOrderIndexByType.get(resolveLeafType(type) ?? type) ??
    Number.MAX_SAFE_INTEGER;

// Initial load order: group by built-in leaf (canonical order), then by code
// (nulls last) and name within the leaf. Applied once on seed to anchor the
// draft array (see seedDraftState) — untouched charts' sortOrder was backfilled
// from this same order, so the canonical (sortOrder-driven) display order
// matches it exactly.
export const compareByLeafThenCodeThenName = (
    a: TypeCodeAndName,
    b: TypeCodeAndName,
): number => {
    const leafDiff = leafOrderOf(a.type) - leafOrderOf(b.type);
    if (leafDiff !== 0) {
        return leafDiff;
    }
    return compareByCodeThenName(a, b);
};
